import { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mic, Users, Gauge, BookOpen, GraduationCap, Mail, Shield,
  CheckCircle2, ArrowRight, Calendar, Clock, Music, MessageSquare,
  Send, Share2, BarChart3, Headphones, Star, FileText,
  Award, TrendingUp, Volume2, Building2, Briefcase, Receipt,
  ShieldCheck, ScrollText, SlidersHorizontal, Palette, Lock,
  UserPlus, Globe,
} from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import Screenshot from "@/components/Screenshot";
import BrowserFrame from "@/components/BrowserFrame";
import WalkthroughPlayer from "@/components/WalkthroughPlayer";
import { walkthroughBySlug } from "@/lib/walkthroughs";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

/* ─── Browser Chrome Wrapper ─── */
/* ─── Soundwave Animation ─── */
const SoundwaveBars = ({ barCount = 16, height = 40, color = "bg-[#f4cf08]/60" }) => {
  const heights = useMemo(
    () => Array.from({ length: barCount }, () => Math.random() * height + 8),
    [barCount, height]
  );
  return (
    <div className="flex items-end justify-center gap-[2px] h-12">
      {heights.map((h, i) => (
        <motion.div
          key={i}
          className={`w-1 ${color} rounded-full`}
          animate={{ height: [4, h, 4] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.06, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
};

/* ─── Mockup: Lesson Summaries ─── */
const LessonSummaryMockup = () => (
  <div className="p-4 md:p-6">
    <div className="mb-4">
      <p className="text-sm md:text-lg font-light text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
        Chopin Nocturne in E-flat Major — Op. 9 No. 2
      </p>
      <div className="flex items-center gap-3 text-[10px] md:text-xs text-zinc-500">
        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Wed, Jan 15</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> 45m 30s</span>
        <span className="text-zinc-400">Emily Chen</span>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="md:col-span-2 space-y-3">
        <div className="bg-white/2 rounded-xl p-3 md:p-4">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-3.5 h-3.5 text-zinc-500" strokeWidth={1.5} />
            <p className="text-[9px] md:text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Summary</p>
          </div>
          <p className="text-[11px] md:text-xs text-zinc-300 leading-relaxed">
            Great progress on the opening section. Rubato is becoming more natural and expressive.
            Focus next week on left-hand accompaniment evenness through the middle section,
            particularly measures 12–20.
          </p>
        </div>
        <div className="bg-[#f4cf08]/3 border border-[#f4cf08]/10 rounded-xl p-3 md:p-4">
          <p className="text-[9px] md:text-[10px] text-[#f4cf08]/70 font-mono uppercase tracking-widest mb-2">Practice Items</p>
          <div className="space-y-1.5">
            {["Slow practice: LH alone mm. 12–20 at 60 BPM", "Record yourself on the B section", "Hands together mm. 45–50 with metronome"].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 text-[#f4cf08]/50 shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-[10px] md:text-xs text-zinc-400">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="bg-white/2 rounded-xl p-3 md:p-4">
          <p className="text-[9px] md:text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-2">Core Concepts</p>
          <div className="flex flex-wrap gap-1.5">
            {["Rubato", "Voicing", "Dynamics", "Phrasing", "Pedaling"].map((c) => (
              <span key={c} className="text-[9px] md:text-[10px] bg-white/5 text-zinc-400 px-2 py-0.5 rounded-full">{c}</span>
            ))}
          </div>
        </div>
        <div className="bg-white/2 rounded-xl p-3 md:p-4 flex items-center gap-2">
          <Mail className="w-3.5 h-3.5 text-emerald-500/60" strokeWidth={1.5} />
          <span className="text-[10px] md:text-xs text-emerald-500/60">Sent to student</span>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Mockup: Student Management ─── */
const StudentManagementMockup = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[10px] md:text-xs text-zinc-500 font-mono uppercase tracking-widest">Your Studio</p>
        <p className="text-sm md:text-lg font-light text-white" style={{ fontFamily: "Outfit, sans-serif" }}>12 Students</p>
      </div>
      <div className="bg-[#f4cf08] rounded-lg px-3 py-1.5 flex items-center gap-1.5">
        <Users className="w-3 h-3 text-black" strokeWidth={1.5} />
        <span className="text-[10px] md:text-xs font-medium text-black">Add Student</span>
      </div>
    </div>
    <div className="space-y-2">
      {[
        { first_name: "Emily", last_name: "Chen", instrument: "Piano", lessons: 24, color: "bg-violet-500/20 text-violet-400" },
        { first_name: "Marcus", last_name: "Rivera", instrument: "Guitar", lessons: 18, color: "bg-blue-500/20 text-blue-400" },
        { first_name: "Sophia", last_name: "Kim", instrument: "Violin", lessons: 31, color: "bg-emerald-500/20 text-emerald-400" },
      ].map((s) => (
        <div key={s.first_name} className="bg-white/2 rounded-xl p-3 flex items-center gap-3">
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/5 flex items-center justify-center text-[10px] md:text-xs text-zinc-400 font-medium">
            {(s.first_name || '?').charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm text-white truncate">{`${s.first_name} ${s.last_name}`.trim()}</p>
            <p className="text-[9px] md:text-[10px] text-zinc-500">{s.instrument}</p>
          </div>
          <span className={`text-[9px] md:text-[10px] px-2 py-0.5 rounded-full ${s.color}`}>
            {s.lessons} lessons
          </span>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Mockup: Repertoire Assistant ─── */
const RepertoireAssistantMockup = () => (
  <div className="p-4 md:p-6 space-y-3">
    <p className="text-[10px] md:text-xs text-zinc-500 font-mono uppercase tracking-widest">Repertoire Assistant</p>
    <div className="bg-white/2 rounded-xl p-3 md:p-4">
      <div className="flex items-start gap-2 mb-3">
        <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center shrink-0">
          <Users className="w-3 h-3 text-zinc-400" strokeWidth={1.5} />
        </div>
        <p className="text-[11px] md:text-xs text-zinc-300 leading-relaxed">
          Emily just finished Chopin's Nocturne Op. 9 No. 2. She's intermediate-advanced, loves Romantic era, and wants to work on voicing. What should she learn next?
        </p>
      </div>
    </div>
    <div className="bg-[#f4cf08]/2 border border-[#f4cf08]/10 rounded-xl p-3 md:p-4">
      <div className="flex items-start gap-2 mb-3">
        <div className="w-6 h-6 rounded-full bg-[#f4cf08]/10 flex items-center justify-center shrink-0">
          <Music className="w-3 h-3 text-[#f4cf08]" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-[11px] md:text-xs text-zinc-200 leading-relaxed mb-2">
            Based on Emily's progress, here are my top recommendations:
          </p>
          <div className="space-y-2">
            {[
              { piece: "Debussy — Clair de Lune", reason: "Expands voicing palette with impressionist textures" },
              { piece: "Chopin — Waltz in C# Minor", reason: "Builds on rubato and phrasing from the Nocturne" },
            ].map((r, i) => (
              <div key={i} className="bg-white/3 rounded-lg p-2">
                <p className="text-[10px] md:text-xs text-white font-medium">{r.piece}</p>
                <p className="text-[9px] md:text-[10px] text-zinc-500">{r.reason}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Mockup: Practice Tools ─── */
const PracticeToolsMockup = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="grid grid-cols-2 gap-3">
      {/* Metronome */}
      <div className="bg-white/2 rounded-xl p-4 text-center">
        <p className="text-[9px] md:text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-3">Metronome</p>
        <p className="text-3xl md:text-4xl font-light text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>120</p>
        <p className="text-[10px] text-zinc-500 mb-3">BPM</p>
        <div className="flex items-center justify-center gap-2 mb-3">
          <span className="text-[10px] bg-white/5 text-zinc-400 px-2 py-0.5 rounded-full">4/4</span>
          <span className="text-[10px] bg-[#f4cf08]/10 text-[#f4cf08] px-2 py-0.5 rounded-full">Tap Tempo</span>
        </div>
        <div className="flex items-center justify-center gap-1">
          {[1,2,3,4].map((beat) => (
            <div key={beat} className={`w-3 h-3 rounded-full ${beat === 1 ? "bg-[#f4cf08]" : "bg-white/10"}`} />
          ))}
        </div>
      </div>
      {/* Tuner */}
      <div className="bg-white/2 rounded-xl p-4 text-center">
        <p className="text-[9px] md:text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-3">Tuner</p>
        <p className="text-3xl md:text-4xl font-light text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>A4</p>
        <p className="text-[10px] text-zinc-500 mb-3">440 Hz</p>
        <div className="relative h-2 bg-white/5 rounded-full mb-3">
          <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-3 h-3 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
        </div>
        <span className="text-[10px] text-emerald-500">In Tune</span>
      </div>
    </div>
  </div>
);

/* ─── Mockup: Email Summaries ─── */
const EmailSummariesMockup = () => (
  <div className="p-4 md:p-6 space-y-3">
    <p className="text-[10px] md:text-xs text-zinc-500 font-mono uppercase tracking-widest">Email Preview</p>
    <div className="bg-white/2 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-zinc-500">To:</p>
            <p className="text-[11px] md:text-xs text-zinc-300">emily.chen@gmail.com</p>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-zinc-500">Subject:</p>
            <p className="text-[11px] md:text-xs text-white">Lesson Summary — Chopin Nocturne Op. 9 No. 2</p>
          </div>
        </div>
        <span className="text-[9px] md:text-[10px] bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} /> Sent
        </span>
      </div>
      <div className="border-t border-white/5 pt-3">
        <p className="text-[11px] md:text-xs text-zinc-300 leading-relaxed mb-2">Hi Emily,</p>
        <p className="text-[11px] md:text-xs text-zinc-400 leading-relaxed">
          Here's your lesson summary from today's session. Great progress on the rubato — the opening section is really coming together. See the practice items below for this week...
        </p>
      </div>
      <div className="bg-[#f4cf08]/3 border border-[#f4cf08]/10 rounded-lg p-2.5">
        <p className="text-[9px] text-[#f4cf08]/70 font-mono uppercase tracking-widest mb-1.5">This Week's Practice</p>
        {["LH alone mm. 12–20 at 60 BPM", "B section: record and listen back"].map((item, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <CheckCircle2 className="w-2.5 h-2.5 text-[#f4cf08]/50" strokeWidth={1.5} />
            <span className="text-[10px] text-zinc-400">{item}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ─── Mockup: Student Portal ─── */
const StudentPortalMockup = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[10px] md:text-xs text-zinc-500 font-mono uppercase tracking-widest">Welcome back</p>
        <p className="text-sm md:text-lg font-light text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Emily</p>
      </div>
      <span className="text-[9px] md:text-[10px] font-mono uppercase tracking-wider bg-violet-500/15 text-violet-400 px-2 py-0.5 rounded-full">Student</span>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[
        { label: "Lessons", value: "24", icon: Mic },
        { label: "Streak", value: "7d", icon: TrendingUp },
        { label: "Pieces", value: "12", icon: Music },
      ].map((s) => (
        <div key={s.label} className="bg-white/2 rounded-xl p-3 text-center">
          <s.icon className="w-3.5 h-3.5 text-zinc-500 mx-auto mb-1" strokeWidth={1.5} />
          <p className="text-lg md:text-xl font-light text-white">{s.value}</p>
          <p className="text-[9px] text-zinc-500">{s.label}</p>
        </div>
      ))}
    </div>
    <div className="bg-white/2 rounded-xl p-3 space-y-2">
      <p className="text-[9px] md:text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Recent Lessons</p>
      {[
        { title: "Chopin Nocturne Op. 9 No. 2", date: "Jan 15" },
        { title: "Scale Fundamentals — A Major", date: "Jan 12" },
        { title: "Sight Reading Practice", date: "Jan 8" },
      ].map((l) => (
        <div key={l.title} className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Mic className="w-3 h-3 text-zinc-600 shrink-0" strokeWidth={1.5} />
            <span className="text-[10px] md:text-xs text-zinc-300 truncate">{l.title}</span>
          </div>
          <span className="text-[9px] text-zinc-600 shrink-0 ml-2">{l.date}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Mockup: Rehearsals ─── */
const RehearsalsMockup = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[10px] md:text-xs text-zinc-500 font-mono uppercase tracking-widest">Practice Session</p>
        <p className="text-sm md:text-lg font-light text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Recording...</p>
      </div>
      <span className="text-[9px] md:text-[10px] bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full flex items-center gap-1">
        <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" /> REC
      </span>
    </div>
    <div className="bg-white/2 rounded-xl p-4">
      <SoundwaveBars barCount={24} height={32} color="bg-[#f4cf08]/50" />
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-zinc-500 font-mono">12:34</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] bg-white/5 text-zinc-400 px-2 py-0.5 rounded-full">Piano</span>
          <span className="text-[10px] bg-white/5 text-zinc-400 px-2 py-0.5 rounded-full">Chopin Nocturne</span>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div className="bg-white/2 rounded-xl p-3">
        <p className="text-[9px] text-zinc-500 font-mono uppercase tracking-widest mb-1">This Week</p>
        <p className="text-lg md:text-xl font-light text-white">3</p>
        <p className="text-[9px] text-zinc-500">sessions</p>
      </div>
      <div className="bg-[#f4cf08]/3 border border-[#f4cf08]/10 rounded-xl p-3">
        <p className="text-[9px] text-[#f4cf08]/70 font-mono uppercase tracking-widest mb-1">Feedback</p>
        <p className="text-[10px] md:text-xs text-[#f4cf08]/80">Awaiting feedback</p>
        <p className="text-[9px] text-zinc-500 mt-0.5">from Sarah (instructor)</p>
      </div>
    </div>
  </div>
);

/* ─── Mockup: Portfolio ─── */
const PortfolioMockup = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[10px] md:text-xs text-zinc-500 font-mono uppercase tracking-widest">Portfolio</p>
        <p className="text-sm md:text-lg font-light text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Emily Chen</p>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] bg-white/5 text-zinc-400 px-2 py-0.5 rounded-full flex items-center gap-1">
          <Share2 className="w-3 h-3" /> Share
        </span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {[
        { title: "Chopin Nocturne Op. 9 No. 2", date: "Jan 2025", tag: "Completed", tagColor: "bg-emerald-500/15 text-emerald-400" },
        { title: "Bach Prelude in C Major", date: "Dec 2024", tag: "Completed", tagColor: "bg-emerald-500/15 text-emerald-400" },
        { title: "Debussy Arabesque No. 1", date: "Nov 2024", tag: "In Progress", tagColor: "bg-[#f4cf08]/15 text-[#f4cf08]" },
        { title: "Mozart Sonata K. 545", date: "Oct 2024", tag: "Completed", tagColor: "bg-emerald-500/15 text-emerald-400" },
      ].map((p) => (
        <div key={p.title} className="bg-white/2 rounded-xl p-3">
          <p className="text-[10px] md:text-xs text-white mb-1 truncate">{p.title}</p>
          <p className="text-[9px] text-zinc-600 mb-2">{p.date}</p>
          <span className={`text-[8px] md:text-[9px] px-1.5 py-0.5 rounded-full ${p.tagColor}`}>{p.tag}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Organization mockups ───
   These use the Studio Quiet design tokens rather than the raw zinc/white classes
   the older marketing mockups were built with. Each mockup is a self-contained
   card, so nothing sits next to a legacy one to clash with, and new marketing
   code should be on-brand. The #f4cf08 accent is unchanged — it is the same
   value as --accent-yellow. */

/* ─── Mockup: Organization Portal ─── */
const OrgPortalMockup = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[10px] md:text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Organization</p>
        <p className="text-sm md:text-lg font-light text-[var(--text-primary)]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Maestro Music Professionals
        </p>
      </div>
      <span className="text-[9px] md:text-[10px] font-mono uppercase tracking-wider bg-[#f4cf08]/15 text-[#f4cf08] px-2 py-0.5 rounded-full">Admin</span>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {[
        { value: "184", label: "Lessons this month", sub: "+22 vs last month" },
        { value: "96", label: "Students", sub: "across 11 instructors" },
        { value: "94%", label: "Consent coverage", sub: "90 of 96 granted" },
        { value: "171", label: "Sent to families", sub: "this month" },
      ].map((s) => (
        <div key={s.label} className="bg-[var(--studio-overlay)] rounded-xl p-3">
          <p className="text-lg md:text-2xl font-light text-[var(--text-primary)] font-mono">{s.value}</p>
          <p className="text-[9px] md:text-[10px] text-[var(--text-secondary)] leading-tight mt-0.5">{s.label}</p>
          <p className="text-[8px] md:text-[9px] text-[var(--text-quaternary)] mt-0.5">{s.sub}</p>
        </div>
      ))}
    </div>
    <div className="bg-[var(--studio-overlay)] rounded-xl p-3 space-y-2">
      <p className="text-[9px] md:text-[10px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Instructors</p>
      {[
        { name: "Sarah Whitfield", meta: "14 students · last lesson today" },
        { name: "Daniel Ortiz", meta: "9 students · last lesson yesterday" },
        // The one badge the portal really shows on an instructor row. Seat tags
        // were deliberately left out: seats are mid-retirement as an entitlement
        // mechanic, and marketing a control that is on its way out ages badly.
        { name: "Priya Raman", meta: "6 students · 2 imported", tag: "Needs review" },
      ].map((i) => (
        <div key={i.name} className="flex items-center gap-3">
          <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-[var(--studio-interactive)] flex items-center justify-center text-[9px] text-[var(--text-secondary)]">
            {i.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] md:text-xs text-[var(--text-primary)] truncate">{i.name}</p>
            <p className="text-[8px] md:text-[9px] text-[var(--text-tertiary)] truncate">{i.meta}</p>
          </div>
          {i.tag && (
            <span className="text-[8px] md:text-[9px] px-2 py-0.5 rounded-full shrink-0 bg-[#f4cf08]/12 text-[#f4cf08]">
              {i.tag}
            </span>
          )}
        </div>
      ))}
    </div>
  </div>
);

/* ─── Mockup: Programs & Recruiting ─── */
const OrgProgramsMockup = () => (
  <div className="p-4 md:p-6 space-y-3">
    <div className="flex items-center justify-between">
      <p className="text-[10px] md:text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Programs</p>
      <span className="text-[10px] md:text-xs bg-[#f4cf08] text-[var(--text-inverse)] px-3 py-1 rounded-lg font-medium">New listing</span>
    </div>
    <div className="bg-[var(--studio-overlay)] rounded-xl p-3 md:p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-xs md:text-sm text-[var(--text-primary)]">After-School Strings — Hendricken</p>
          <p className="text-[9px] md:text-[10px] text-[var(--text-tertiary)] mt-0.5">Violin, Viola, Cello · Providence, RI · Starts Sep 8</p>
        </div>
        <span className="text-[8px] md:text-[9px] bg-[var(--state-success)]/15 text-[var(--state-success)] px-2 py-0.5 rounded-full shrink-0">Open</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {["Visible to all instructors", "6 members", "3 applications"].map((t) => (
          <span key={t} className="text-[8px] md:text-[9px] bg-[var(--studio-interactive)] text-[var(--text-secondary)] px-2 py-0.5 rounded-full">{t}</span>
        ))}
      </div>
    </div>
    <div className="bg-[var(--studio-overlay)] rounded-xl p-3 space-y-2">
      <p className="text-[9px] md:text-[10px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Applications</p>
      {[
        { name: "Daniel Ortiz", note: "12 years teaching cello, Suzuki certified" },
        { name: "Priya Raman", note: "Available Tue/Thu afternoons" },
      ].map((a) => (
        <div key={a.name} className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] md:text-xs text-[var(--text-primary)] truncate">{a.name}</p>
            <p className="text-[8px] md:text-[9px] text-[var(--text-tertiary)] truncate">{a.note}</p>
          </div>
          <span className="text-[8px] md:text-[9px] bg-[#f4cf08]/12 text-[#f4cf08] px-2 py-0.5 rounded-full shrink-0">Accept</span>
          <span className="text-[8px] md:text-[9px] bg-[var(--studio-interactive)] text-[var(--text-tertiary)] px-2 py-0.5 rounded-full shrink-0">Decline</span>
        </div>
      ))}
    </div>
    <div className="bg-[#f4cf08]/3 border border-[#f4cf08]/10 rounded-xl p-3 flex items-center gap-2">
      <CheckCircle2 className="w-3.5 h-3.5 text-[#f4cf08]/60 shrink-0" strokeWidth={1.5} />
      <p className="text-[9px] md:text-[11px] text-[var(--text-secondary)]">
        Lessons scheduled under this program are attributed to your organization automatically
      </p>
    </div>
  </div>
);

/* ─── Mockup: Consent & Compliance ─── */
const OrgComplianceMockup = () => (
  <div className="p-4 md:p-6 space-y-3">
    <div className="flex items-center justify-between">
      <p className="text-[10px] md:text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Consent ledger</p>
      <span className="text-[8px] md:text-[9px] bg-[var(--studio-interactive)] text-[var(--text-secondary)] px-2 py-0.5 rounded-full">Export CSV</span>
    </div>
    <div className="bg-[var(--studio-overlay)] rounded-xl overflow-hidden">
      {[
        { student: "Emily Chen", band: "13–17", event: "Granted", when: "Jan 6", ok: true },
        { student: "Noah Whitfield", band: "Under 13", event: "Granted by parent", when: "Jan 8", ok: true },
        { student: "Marcus Rivera", band: "Adult", event: "Granted", when: "Jan 9", ok: true },
        { student: "Ava Delgado", band: "Under 13", event: "Requested", when: "Jan 11", ok: false },
      ].map((r, i) => (
        <div
          key={r.student}
          className={`flex items-center gap-3 px-3 py-2 ${i > 0 ? "border-t border-[var(--studio-divider)]" : ""}`}
        >
          <div className="flex-1 min-w-0">
            <p className="text-[10px] md:text-xs text-[var(--text-primary)] truncate">{r.student}</p>
            <p className="text-[8px] md:text-[9px] text-[var(--text-tertiary)]">{r.band}</p>
          </div>
          <span
            className={`text-[8px] md:text-[9px] px-2 py-0.5 rounded-full shrink-0 ${
              r.ok
                ? "bg-[var(--state-success)]/15 text-[var(--state-success)]"
                : "bg-[#f4cf08]/12 text-[#f4cf08]"
            }`}
          >
            {r.event}
          </span>
          <span className="text-[8px] md:text-[9px] text-[var(--text-quaternary)] shrink-0 w-10 text-right">{r.when}</span>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <div className="bg-[var(--studio-overlay)] rounded-xl p-3 flex items-start gap-2">
        <Lock className="w-3.5 h-3.5 text-[var(--text-tertiary)] shrink-0 mt-0.5" strokeWidth={1.5} />
        <p className="text-[9px] md:text-[11px] text-[var(--text-secondary)] leading-relaxed">
          Recording is blocked until consent is granted — enforced on the server, per student
        </p>
      </div>
      <div className="bg-[var(--studio-overlay)] rounded-xl p-3 flex items-start gap-2">
        <ShieldCheck className="w-3.5 h-3.5 text-[var(--text-tertiary)] shrink-0 mt-0.5" strokeWidth={1.5} />
        <p className="text-[9px] md:text-[11px] text-[var(--text-secondary)] leading-relaxed">
          Under-13 summaries go to the parent alone, and lesson audio is deleted after processing
        </p>
      </div>
    </div>
  </div>
);

/* ─── Feature Data ─── */
const featureData = {
  "lesson-summaries": {
    icon: Mic,
    title: "Lesson summaries",
    subtitle: "Because 'I think we worked on scales?' isn't a lesson plan",
    audience: "Instructors",
    description: [
      "Hans captures your lessons and turns them into clear, actionable summaries. No typing, no scrambling for a pen between students — just teach.",
      "Each summary includes what you covered, what to practice, and core concepts. A draft is ready a few minutes after your lesson ends, for you to review and share.",
    ],
    features: [
      "Real-time audio recording with background processing",
      "Transcription and a first draft of the notes",
      "Automatic extraction of practice items and key concepts",
      "Lesson history with searchable archives",
      "One-click email delivery to students and parents",
      "Lesson notes and summaries stored indefinitely — audio is never kept long-term",
    ],
    screenshot: { src: "/marketing/lesson-summary.webp", alt: "A lesson summary in Hans: overview, student focus, practice items and the instructor's own notes." },
    mockup: <LessonSummaryMockup />,
    quote: "I used to spend 20 minutes after each lesson writing notes. Now I just teach.",
    carousel: [
      { icon: Mic, title: "Record & Transcribe", desc: "Hit record, teach your lesson. Hans handles the rest — the transcript and the first draft of the notes are written up in the background.", visual: <Mic className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1.5} /> },
      { icon: FileText, title: "Clear Summaries", desc: "Not a transcript dump. Hans picks out what matters: key concepts, practice assignments, and progress notes — organized and readable.", visual: <BookOpen className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1.5} /> },
      { icon: Send, title: "Review and Share", desc: "Read the draft, edit anything you'd put differently, and send a polished summary to your student (and their parents) with one click. They see exactly what to practice and why.", visual: <Mail className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1.5} /> },
    ],
  },
  "student-management": {
    icon: Users,
    title: "Student Management",
    subtitle: "Your studio, minus the spreadsheet",
    audience: "Instructors",
    description: [
      "Every student's info, lesson history, and progress in one place. No more sticky notes on the piano. No more 'wait, did we cover that last week?'",
      "Whether you teach 3 students or 30, Hans keeps everything organized, searchable, and actually useful when you're prepping for the next lesson.",
    ],
    features: [
      "Detailed student profiles with contact information",
      "Complete lesson history per student",
      "Personal notes and progress tracking",
      "Instrument and repertoire tracking",
      "5 active students on Solo, unlimited on any paid plan",
      "Quick-access student roster with search",
    ],
    screenshot: { src: "/marketing/roster.webp", alt: "The student roster in Hans, with each student's instrument, consent state and last lesson." },
    mockup: <StudentManagementMockup />,
    quote: "I finally stopped mixing up which student is working on which Sonatina.",
    carousel: [
      { icon: Users, title: "Organized Profiles", desc: "Name, instrument, contact info, lesson history — all in one place. Search by name or instrument and find anyone instantly.", visual: <Users className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: BarChart3, title: "Progress at a Glance", desc: "See how many lessons each student has had, what they've been working on, and where they're headed. No digging required.", visual: <TrendingUp className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: BookOpen, title: "Lesson History", desc: "Every lesson summary, practice item, and concept — linked to the student who learned it. Prep for lessons in 30 seconds flat.", visual: <Clock className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
  "lesson-history": {
    icon: BarChart3,
    title: "Lesson History & Reporting",
    subtitle: "Answer \"how much did I teach last month?\" without opening a spreadsheet",
    audience: "Instructors",
    description: [
      "Every lesson you have ever taught, in one place, filtered to the month, quarter or year you care about. Alongside the list: how many lessons, how many teaching hours, how many a week, and which students you have actually been seeing.",
      "It also surfaces the thing that is easy to lose track of — the lessons whose notes never went out. One tap filters the list to your backlog so nobody's family is left waiting on a summary you thought you sent.",
    ],
    features: [
      "Individual and group lessons in one timeline, grouped by month",
      "Filter by month, quarter, year or all time — the totals follow the filter",
      "Lessons, teaching hours, lessons per week, and lessons per student at a glance",
      "A clear mark on every lesson showing whether the notes reached the family",
      "One tap to see only the summaries you still owe",
      "Export to a spreadsheet, choosing exactly which columns you want",
      "Search by lesson title, student or class",
    ],
    screenshot: {
      src: "/marketing/lessons.webp",
      alt: "The Lessons page in Hans: a month-grouped lesson history beside teaching totals and charts.",
    },
    quote: "My accountant asked how many hours I taught last year. It took me about four seconds.",
    carousel: [
      { icon: BarChart3, title: "The numbers, without the spreadsheet", desc: "Lessons taught, hours at the instrument, and your weekly average — recalculated the moment you change the period. Nothing to tally by hand at the end of a term.", visual: <BarChart3 className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> }, // brand-lint-ignore — matches the 30 other carousel visuals in this file; they migrate together
      { icon: CheckCircle2, title: "Nothing slips through", desc: "Every lesson shows whether its notes reached the family. The ones that didn't are counted, and one tap filters the list down to just those.", visual: <CheckCircle2 className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> }, // brand-lint-ignore — matches the 30 other carousel visuals in this file; they migrate together
      { icon: FileText, title: "Export what you need", desc: "Taxes, a school report, your own records — pick the columns and take a spreadsheet with you. It opens in Excel, Numbers or Google Sheets.", visual: <FileText className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> }, // brand-lint-ignore — matches the 30 other carousel visuals in this file; they migrate together
    ],
  },
  "repertoire-assistant": {
    icon: BookOpen,
    title: "Repertoire Assistant",
    subtitle: "When you know the student needs 'something after Burgmüller' but can't place it",
    audience: "Instructors",
    description: [
      "Not sure what to assign next? Hans knows your student's level, their preferences, and what they've already covered. It recommends pieces that actually make sense for where they are.",
      "Guides built around each student's level, goals and repertoire, ready before the lesson — with practice strategies, technical focus areas, and progression paths.",
    ],
    features: [
      "Repertoire recommendations matched to each student",
      "Difficulty-matched piece suggestions",
      "Teaching guides with practice strategies",
      "Technical focus area identification",
      "Genre and style diversity tracking",
      "Integration with student lesson history",
    ],
    screenshot: { src: "/marketing/repertoire.webp", alt: "The repertoire assistant in Hans, generating a teaching guide for a student." },
    mockup: <RepertoireAssistantMockup />,
    quote: "It's like having a colleague who's memorized every piece in the Henle catalog.",
    carousel: [
      { icon: MessageSquare, title: "Ask Hans Anything", desc: "Describe what your student needs — by level, style, technique, or just vibes. Hans responds with specific, practical recommendations.", visual: <MessageSquare className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: BookOpen, title: "Teaching Guides", desc: "Each recommendation includes practice strategies, technical focus areas, and notes on why this piece is right for this student at this time.", visual: <BookOpen className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: TrendingUp, title: "Progression Paths", desc: "See where a piece fits in your student's development. Build a repertoire arc, not just a list.", visual: <TrendingUp className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
  "practice-tools": {
    icon: Gauge,
    title: "Practice Tools",
    subtitle: "The metronome and tuner that are already open when you need them",
    audience: "Instructors",
    description: [
      "Every studio needs a metronome and tuner that just work. Hans has both built in — no separate apps, no ads, no subscriptions-within-your-subscription.",
      "Tap tempo, customizable time signatures, chromatic tuner with real-time pitch detection. Works on any device, available from the student portal too.",
    ],
    features: [
      "Professional metronome with tap tempo",
      "Customizable time signatures and subdivisions",
      "Chromatic tuner with real-time pitch detection",
      "Visual and audio feedback",
      "Works on desktop and mobile",
      "Accessible from the student portal too",
    ],
    screenshot: { src: "/marketing/tools.webp", alt: "The metronome and chromatic tuner built into Hans." },
    mockup: <PracticeToolsMockup />,
    quote: "Honestly I just needed a metronome that didn't show me ads for insurance.",
    carousel: [
      { icon: Gauge, title: "Metronome", desc: "BPM control, tap tempo, and visual beat indicators. Set your time signature and subdivisions — it's a real metronome, not a toy.", visual: <Gauge className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: Volume2, title: "Chromatic Tuner", desc: "Real-time pitch detection with visual feedback. See the note, see the cents, get in tune. That's it.", visual: <Volume2 className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
  // Led by the instructor walkthrough rather than a screenshot: joining is a
  // sequence (invitation, program, class), and the film shows it in order.
  "joining-a-program": {
    icon: UserPlus,
    title: "Joining a Program",
    subtitle: "Accept your organization's invitation, or find a program to teach in. Here's your first week, start to finish.",
    audience: "Instructors",
    walkthrough: "instructor-first-week",
    description: [
      "Organizations run their programs on Hans: the classes, the schedule and the pay. When one invites you, the invitation waits in Programs, under Invites, with the pay, the dates and the place on it. Accept it, and the program and the classes your organization assigns you appear under My programs.",
      "Looking for work? Opportunities lists programs taking instructors near you, on a list or a map. Apply with an optional note to the organization. Applying tells them you're interested; they decide who joins, so it isn't the same as an invitation. And if you'd like organizations to find you, turn on Open to teaching work in your profile.",
    ],
    features: [
      "Invitations waiting in Programs, under Invites",
      "Pay, dates and location shown before you accept",
      "Your classes and every upcoming session, as your organization assigns them",
      "Opportunities near you, on a list or a map",
      "Apply with an optional note, and withdraw while it's pending",
      "Prep notes for any session that hasn't started yet",
    ],
    carousel: [
      { icon: Mail, title: "Invites", desc: "An organization's invitation comes by email and waits under Programs, then Invites. Each program invite shows the pay, instruments, dates and place, and accepting or declining is one press.", visual: <Mail className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1} /> },
      { icon: Globe, title: "Opportunities", desc: "Programs looking for instructors, filtered by how far you'll travel and shown on a list or a map. Open one to read the details, then apply with a short note.", visual: <Globe className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1} /> },
      { icon: Briefcase, title: "My programs", desc: "Every program you teach in, with the classes your organization has assigned you, its contacts, and what to know before your first day.", visual: <Briefcase className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1} /> },
      { icon: FileText, title: "Prep notes", desc: "Plan a session before it starts. Your organization can see your plan beside its own note; families never do, and nothing counts as taught until the lesson is written.", visual: <FileText className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1} /> },
    ],
  },
  "email-summaries": {
    icon: Mail,
    title: "Email Summaries",
    subtitle: "The recap your students' parents actually read",
    audience: "Instructors",
    description: [
      "After every lesson, send a polished summary directly to your students and their parents. No more handwritten notes getting lost in backpacks.",
      "Each email includes what you covered, what to practice, and the key concepts — formatted and professional. One click, done.",
    ],
    features: [
      "One-click email delivery after each lesson",
      "Professionally formatted summary emails",
      "Includes practice assignments and key concepts",
      "Reliable delivery via Resend",
      "Email history tracking per student",
      "Customizable sender information",
    ],
    screenshot: { src: "/marketing/email-preview.webp", alt: "Previewing the summary email exactly as the family will receive it, before sending." },
    mockup: <EmailSummariesMockup />,
    quote: "Parents started thanking me for the 'detailed notes.' I didn't have the heart to tell them.",
    carousel: [
      { icon: Send, title: "One-Click Send", desc: "Lesson done? Summary reviewed? Hit send. Your student and their parents get a clean, professional recap before you start your next lesson.", visual: <Send className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1.5} /> },
      { icon: Mail, title: "Professional Format", desc: "No generic template. Each email reflects the actual lesson content — summary, practice items, and concepts, formatted for readability.", visual: <FileText className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: Clock, title: "Email History", desc: "See every email you've sent per student. Know what was communicated, when, and to whom. No more 'did I already send that?'", visual: <Clock className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
  "student-portal": {
    icon: GraduationCap,
    title: "Student Dashboard",
    subtitle: "Everything from your lessons, without texting your teacher at 10 PM",
    audience: "Students",
    description: [
      "Your own space to review lesson summaries, track practice assignments, and see how far you've come. No more 'what was I supposed to practice again?'",
      "Secure login with an invite from your instructor. All your lessons, practice history, and portfolio in one place — on any device.",
    ],
    features: [
      "Personal dashboard with lesson overview",
      "Every lesson summary your instructor shares",
      "Practice assignment tracking",
      "Progress visualization over time",
      "Secure invite-based registration",
      "Works on any device with a browser",
    ],
    screenshot: { src: "/marketing/student-dashboard.webp", alt: "The student portal dashboard: recent lessons, practice streak and what to work on." },
    mockup: <StudentPortalMockup />,
    quote: "I actually know what to practice between lessons now. It's weird.",
    carousel: [
      { icon: GraduationCap, title: "Your Dashboard", desc: "See your recent lessons, practice streak, and upcoming focus areas. Everything you need at a glance — no scrolling through texts or emails.", visual: <GraduationCap className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: BookOpen, title: "Lesson Summaries", desc: "Every lesson your instructor recorded is here — what you covered, what to practice, and the concepts behind it. Review anytime.", visual: <BookOpen className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: TrendingUp, title: "Track Your Progress", desc: "See how many lessons you've had, your practice consistency, and your repertoire growth. Progress you can actually see.", visual: <BarChart3 className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
  "rehearsals": {
    icon: Headphones,
    title: "Rehearsals",
    subtitle: "Record your practice sessions and hear back from your instructor",
    audience: "Students",
    description: [
      "Practice between lessons and actually get feedback on it. Record yourself, tag what you worked on, and your instructor can listen and respond — no extra lesson time needed.",
      "Track what you worked on, for how long, and how it felt. See your practice consistency build over time. When your instructor leaves feedback, you'll know.",
    ],
    features: [
      "Record practice sessions from any device",
      "Instructor listens and provides async feedback",
      "Duration and focus area tracking",
      "Practice streak and consistency visualization",
      "Instrument-specific practice logging",
      "Historical practice data and trends",
    ],
    screenshot: { src: "/marketing/student-rehearsals.webp", alt: "A student's rehearsal recordings in Hans, with AI feedback and their instructor's notes." },
    mockup: <RehearsalsMockup />,
    quote: "My teacher heard me nail the passage I'd been stuck on for weeks — without waiting until Thursday.",
    carousel: [
      { icon: Mic, title: "Record Yourself", desc: "Hit record, play through your practice, and tag what you worked on. Hans captures the audio and tracks the session details automatically.", visual: <Mic className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: Headphones, title: "Instructor Feedback", desc: "Your instructor listens on their own time and leaves feedback. No scheduling conflicts, no extra fees. Just asynchronous coaching that works.", visual: <MessageSquare className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: BarChart3, title: "Practice Trends", desc: "See your practice frequency, duration, and focus areas over time. Build streaks, spot patterns, and stay accountable to your goals.", visual: <TrendingUp className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
  "portfolio": {
    icon: Shield,
    title: "Portfolio",
    subtitle: "Your musical resume, built as you learn",
    audience: "Students",
    description: [
      "Every piece you've learned, every milestone you've hit — collected automatically into a portfolio you can actually share. No retroactive scrambling before auditions.",
      "Your portfolio fills itself from your lesson history. Share it with a link or export it as a PDF. Family, friends, admissions committees — they can all see the work.",
    ],
    features: [
      "Automatic portfolio generation from lesson data",
      "Performance highlights and milestones",
      "Shareable public portfolio link",
      "Repertoire showcase with progress notes",
      "Customizable profile information",
      "PDF export for auditions and applications",
    ],
    screenshot: { src: "/marketing/student-portfolio.webp", alt: "A student's portfolio page in Hans, showing repertoire, milestones and interests." },
    mockup: <PortfolioMockup />,
    quote: "Audition applications used to take hours. Now I just send my portfolio link.",
    carousel: [
      { icon: Award, title: "Automatic Highlights", desc: "As you complete pieces and hit milestones, your portfolio updates itself. No manual entry, no forgetting what you played two years ago.", visual: <Star className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: Share2, title: "Shareable Link", desc: "Generate a public portfolio link and share it with anyone — family, friends, college admissions, festival applications. Your work, visible.", visual: <Share2 className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: FileText, title: "PDF Export", desc: "Need a printed portfolio for an audition or application? Export a clean PDF with your repertoire, progress notes, and timeline.", visual: <FileText className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
  "organization-portal": {
    icon: Building2,
    title: "Organization Portal",
    subtitle: "See what your instructors are delivering, without asking them for a report",
    audience: "Organizations",
    description: [
      "Schools, studios, and programs get their own portal in Hans — a separate login from your instructors' accounts, with its own admin team. It opens on the week ahead: the next seven days of class meetings, who is teaching each one, who is covering, and anything called off. Below it, the month — lessons delivered, students reached, summaries sent, and how many families have recording consent on file.",
      "You also set the rules. Turn Hans features on or off for the whole organization, override them for an individual instructor, give a sponsor or coordinator read-only sight of one program, and read an append-only log of every administrative action — including which admin opened which lesson.",
    ],
    features: [
      "A dashboard of sessions taught, active programs, instructors teaching and students, over any range you choose",
      "Every session across every program in one list, and every student on your rosters in another",
      "A CSV of any dashboard tab, whenever you want it",
      "A calendar of every class meeting across every program, coloured by program and filterable by instructor",
      "Active students for the billing month, with the names behind the count and the state it is in",
      "Read-only lesson summaries for program students — transcripts and instructors' private notes are never exposed",
      "Organization-wide feature policy with per-instructor overrides",
      "Read-only viewers scoped to named programs, with permissions granted one at a time",
      "Self-managed admin team: invite colleagues, deactivate access instantly",
      "Append-only activity ledger recording every admin action, including lesson views",
      "Co-branding — your logo, name, accent color and custom domain on what families receive",
    ],
    screenshot: { src: "/marketing/org-dashboard.webp", alt: "The organization dashboard: sessions taught, active programs, instructors teaching and students, over a chart of sessions by week." },
    mockup: <OrgPortalMockup />,
    carousel: [
      { icon: BarChart3, title: "Oversight, not surveillance", desc: "Sessions, programs, instructors and students across your whole organization — the numbers a director needs, collected as instructors work rather than chased at month end.", visual: <TrendingUp className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1.5} /> },
      { icon: SlidersHorizontal, title: "Policy you control", desc: "Decide which parts of Hans your instructors get — scheduling, the public booking page, adding their own students — organization-wide, with exceptions per instructor.", visual: <SlidersHorizontal className="w-8 h-8 text-[var(--accent-yellow-text)] opacity-40" strokeWidth={1.5} /> },
      { icon: ScrollText, title: "An audit trail by default", desc: "Every branding change, feature flip, membership change, and lesson view is written to a ledger that can't be edited or deleted. When someone asks who saw what, there's an answer.", visual: <ScrollText className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: Palette, title: "Your name on the summary", desc: "Lesson emails for your programs carry your logo, organization name, and accent color, so the family's inbox reflects who they enrolled with.", visual: <Palette className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
  "organization-programs": {
    icon: Briefcase,
    title: "Programs & Classes",
    subtitle: "Publish it, staff it, schedule the term, and let the lessons roll up on their own",
    audience: "Organizations",
    description: [
      "Publish the programs you run — an after-school strings block, a summer intensive, a partner-school residency — and instructors on Hans can apply. Accept the ones you want and they join that program. Families can find the program too: every open program gets a public page where they browse your instructors and book directly.",
      "Then build the term. A class's meetings come from a weekly pattern or a list of dates, with the district's closure days imported from its calendar file rather than retyped, and every date previewed before the first write. Fill the roster from a spreadsheet, assign substitutes for the meetings the owner cannot take, and take the whole term back out as one row per meeting.",
      "The part that saves the most work is what happens afterward. When a program lesson is taught on the schedule, it's attributed to your organization automatically — counted on your dashboard, visible in your portal, and billable — with no timesheets and no monthly tally.",
    ],
    features: [
      "Program types that behave differently: private lessons, classes, and camps with a fixed run",
      "Control who can see and apply: every Hans instructor, or a named invite list",
      "Review, accept, or decline applications; add or remove members at any time",
      "Classes owned by one instructor, reassignable mid-run without rewriting taught history",
      "A term from a weekly pattern or a list of dates, always previewed before it is written",
      "The school calendar imported as an .ics file or a pasted column — holidays excluded up front",
      "Substitutes assigned per meeting, shown on the calendar and carried into the export",
      "Roster import from CSV or paste, resolved row by row before anything is created",
      "A term export: one row per meeting with instructor, substitute, status, attendance count, minutes and summary",
      "Weekly recap and week-ahead emails per program, on a schedule the organization sets",
      "Public program directory where families browse instructors and book a lesson",
      "Automatic attribution — a scheduled program lesson counts toward your organization",
      "Instructors keep their private students entirely separate from your programs",
    ],
    screenshot: { src: "/marketing/org-program-detail.webp", alt: "A program page in the Hans organization portal, showing its instructors, its classes and what has been taught in it." },
    mockup: <OrgProgramsMockup />,
    carousel: [
      { icon: UserPlus, title: "Recruit inside Hans", desc: "Instructors already using Hans see your listing, apply with a note, and are teaching under your program the same day — no job board, no onboarding thread, no separate account for them to set up.", visual: <UserPlus className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: Globe, title: "A public page for every program", desc: "Families browse the program, see each instructor's bio, photo, and lesson formats, and book an opening straight from their real availability.", visual: <Globe className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: CheckCircle2, title: "Attribution without paperwork", desc: "Scheduled, attributed, recorded, billable — one chain. If a lesson happened under your program, it's in your numbers; if it didn't, it never touches them.", visual: <BarChart3 className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
  "organization-billing": {
    icon: Receipt,
    title: "Tuition invoicing retired",
    subtitle: "Hans no longer creates or sends tuition invoices",
    audience: "Organizations",
    description: [
      "Hans no longer creates or sends tuition invoices. Recurring tuition plans, payment-account onboarding and overdue reminders have been retired.",
      "Your organization's Hans subscription and recording credits are managed separately, under Settings in the organization portal.",
    ],
    features: ["Existing invoice records retained"],
    carousel: [],
  },
  "organization-compliance": {
    icon: ShieldCheck,
    title: "Consent & Compliance",
    subtitle: "Recording children is the part you cannot afford to get wrong",
    audience: "Organizations",
    description: [
      "Hans records lessons, and most students in a school program are minors. So consent isn't a checkbox here — it's a gate. No instructor in your organization can start a recording for a student until that student's consent is granted, and every request, grant, decline, and revocation is written to an append-only ledger you can export as a CSV.",
      "Under-13 students follow COPPA specifically: a direct notice to the parent, a confirmation email with a cancel link, and summaries delivered to the parent alone. Full lesson recordings are deleted after processing succeeds or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day cleanup backstop. Consented practice clips are kept separately for up to 30 days.",
    ],
    features: [
      "Recording blocked until consent is granted — enforced server-side, per student",
      "Age bands (adult, 13–17, under 13) decide who is asked and who receives summaries",
      "COPPA direct notice plus a parent confirmation email for every under-13 student",
      "Paper consent can be recorded with an attestation, and it lands in the same ledger",
      "Append-only consent ledger with CSV export for your own records and audits",
      "Every summary email carries a revoke link; revoking deletes remaining audio and transcripts",
      "Full lesson recordings deleted after processing; consented practice clips expire after 30 days",
      "Instructors join your organization by accepting an invite, and can leave on their own",
      "Your visibility is scoped to program work — an instructor's private students never appear",
    ],
    screenshot: { src: "/marketing/consent-ledger.webp", alt: "The append-only consent ledger in Hans, with CSV export." },
    mockup: <OrgComplianceMockup />,
    carousel: [
      { icon: Lock, title: "Consent is a gate, not a note", desc: "The check runs on the server every time a recording starts. An instructor who hasn't collected consent doesn't get a warning they can dismiss — they get a blocked recording and a way to send the request.", visual: <Lock className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: ShieldCheck, title: "COPPA, handled specifically", desc: "Under-13 students get a direct parental notice, a confirmation email with a cancel link, and parent-only summary delivery. A child never holds the link that could revoke their own consent.", visual: <ShieldCheck className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: ScrollText, title: "Evidence you can hand over", desc: "The ledger is append-only and exports to CSV: who consented, when, under which version of the agreement, and by what method. That is the artifact an audit or a worried parent actually asks for.", visual: <FileText className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
      { icon: Users, title: "A boundary instructors trust", desc: "You see the students and lessons attributed to your programs. Their private roster stays theirs, and Hans tells them so — which is what makes instructors willing to join in the first place.", visual: <Users className="w-8 h-8 text-[#f4cf08]/40" strokeWidth={1} /> },
    ],
  },
};

/** Every slug `/features/:featureSlug` will render. Anything else redirects home. */
export const FEATURE_SLUGS = Object.keys(featureData);

/** slug → its product screenshot, so a test can check the files exist. */
export const FEATURE_SCREENSHOTS = Object.fromEntries(
  Object.entries(featureData)
    .filter(([, f]) => f.screenshot)
    .map(([slug, f]) => [slug, f.screenshot])
);

/** slug → the walkthrough a page leads with instead of a screenshot. */
export const FEATURE_WALKTHROUGHS = Object.fromEntries(
  Object.entries(featureData)
    .filter(([, f]) => f.walkthrough)
    .map(([slug, f]) => [slug, f.walkthrough])
);

/** slug → audience, so a test can check every page has a CTA that fits it. */
export const FEATURE_AUDIENCES = Object.fromEntries(
  Object.entries(featureData).map(([slug, f]) => [slug, f.audience])
);

// The CTA has to match how each audience actually gets in. Instructors and
// students and organizations each have their own signup or invite flow.
export const CTAS = {
  Instructors: { blurb: "Sign up as an instructor and start using Hans today.", to: "/login", label: "Get Started" },
  Students: { blurb: "Ask your instructor for an invite to the student portal.", to: "/student/login", label: "Get Started" },
  Organizations: {
    blurb: "Create your organization, invite your instructors, and set up scheduling.",
    to: "/org/signup",
    label: "Create Organization",
  },
};

const FeaturePage = () => {
  const { featureSlug } = useParams();

  // Redirect old practice-logs URL
  if (featureSlug === "practice-logs") {
    return <Navigate to="/features/rehearsals" replace />;
  }

  const feature = featureData[featureSlug];

  if (featureSlug === "organization-billing") {
    return (
      <PublicLayout>
        <main className="mx-auto max-w-2xl space-y-6 px-6 py-20 text-[var(--text-primary)]">
          <h1 className="font-serif text-3xl">Tuition invoicing retired</h1>
          <p className="text-[var(--text-secondary)]">Hans no longer creates or sends tuition invoices. Your organization&apos;s Hans plan is under Settings in the organization portal.</p>
          <Link to="/org" className="inline-flex rounded-full border border-[var(--studio-divider)] px-6 py-3 transition duration-200 active:scale-95">Open the organization portal</Link>
        </main>
      </PublicLayout>
    );
  }

  if (!feature) {
    return <Navigate to="/" replace />;
  }

  const walkthrough = feature.walkthrough ? walkthroughBySlug(feature.walkthrough) : null;

  // Falls back rather than indexing straight into CTAS: adding a feature page is
  // a DATA edit, and an audience string the map doesn't know would otherwise
  // dereference undefined and blank the page — the same reason an unknown slug
  // redirects above instead of throwing. The test pins every entry's audience,
  // so this fallback should never be the branch that runs.
  const cta = CTAS[feature.audience] ?? CTAS.Instructors;

  return (
    <PublicLayout>
      <main className="relative z-10 px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-32 max-w-5xl mx-auto safe-area-padding">
        {/* ─── Hero ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-14 md:mb-20"
        >
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#f4cf08]/70 mb-3 block">
            For {feature.audience}
          </span>
          <h1
            className="text-3xl md:text-5xl font-light tracking-tight leading-tight mb-4"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {feature.title}
          </h1>
          <p className="text-base md:text-lg text-zinc-400 max-w-xl mx-auto">
            {feature.subtitle}
          </p>
        </motion.div>

        {/* ─── Product Mockup, or the walkthrough the page leads with ─── */}
        {walkthrough ? (
          <motion.section {...fadeInUp} className="mb-16 md:mb-24">
            <WalkthroughPlayer walkthrough={walkthrough} />
          </motion.section>
        ) : (
          <section className="relative mb-16 md:mb-24">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none">
              <div className="w-[600px] h-[400px] mx-auto rounded-full" style={{ background: "radial-gradient(ellipse, var(--accent-yellow-faint) 0%, transparent 70%)" }} />
            </div>
            <motion.div {...fadeInUp} className="relative" style={{ perspective: "1200px" }}>
              <BrowserFrame>
                {feature.screenshot ? (
                  <Screenshot src={feature.screenshot.src} alt={feature.screenshot.alt} />
                ) : (
                  feature.mockup
                )}
              </BrowserFrame>
            </motion.div>
          </section>
        )}

        {/* ─── Description ─── */}
        <motion.div {...fadeInUp} className="space-y-4 mb-14 md:mb-20 max-w-2xl mx-auto">
          {feature.description.map((paragraph, i) => (
            <p key={i} className="text-zinc-400 text-sm md:text-base leading-relaxed text-center">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* ─── Carousel — "See it in action" ─── */}
        <motion.div {...fadeInUp} className="mb-16 md:mb-24">
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-6 text-center">
            See it in action
          </p>
          <div className="px-8 md:px-14">
            <Carousel opts={{ align: "start", loop: true }}>
              <CarouselContent>
                {feature.carousel.map((slide, i) => {
                  const SlideIcon = slide.icon;
                  return (
                    <CarouselItem key={i} className="md:basis-1/2 lg:basis-1/2">
                      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 h-full flex flex-col">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center">
                            <SlideIcon className="w-4 h-4 text-[#f4cf08]" strokeWidth={1.5} />
                          </div>
                          <h3 className="text-sm md:text-base font-medium text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {slide.title}
                          </h3>
                        </div>
                        <p className="text-xs md:text-sm text-zinc-400 leading-relaxed flex-1">
                          {slide.desc}
                        </p>
                        <div className="mt-4 flex justify-center opacity-30">
                          {slide.visual}
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="border-white/10 bg-zinc-900/80 hover:bg-zinc-800 text-white" />
              <CarouselNext className="border-white/10 bg-zinc-900/80 hover:bg-zinc-800 text-white" />
            </Carousel>
          </div>
        </motion.div>

        {/* ─── Features List ─── */}
        <motion.div {...fadeInUp} className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 md:p-8 mb-14 md:mb-20">
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-5">What's Included</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {feature.features.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="flex items-start gap-3"
              >
                <CheckCircle2 className="w-4 h-4 text-[#f4cf08]/60 shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-sm text-zinc-300">{item}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ─── Pull Quote ─── */}
        {feature.quote && (
          <motion.div {...fadeInUp} className="mb-14 md:mb-20">
            <div className="flex items-center gap-4 md:gap-6 justify-center">
              <div className="flex-1 h-px bg-linear-to-r from-transparent to-white/10" />
              <p className="text-sm md:text-base text-zinc-400 italic max-w-lg text-center leading-relaxed">
                "{feature.quote}"
              </p>
              <div className="flex-1 h-px bg-linear-to-l from-transparent to-white/10" />
            </div>
          </motion.div>
        )}

        {/* ─── CTA ─── */}
        <motion.div {...fadeInUp} className="text-center">
          <h2
            className="text-xl md:text-2xl font-light tracking-tight mb-4"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Ready to get started?
          </h2>
          <p className="mb-6 text-sm text-[var(--text-tertiary)]">{cta.blurb}</p>
          <Link
            to={cta.to}
            className="inline-flex items-center gap-2 bg-[#f4cf08] text-black font-medium rounded-full px-8 py-3 text-sm
                       hover:scale-105 active:scale-95 transition-transform shadow-[0_0_40px_rgba(244,207,8,0.25)]"
          >
            {cta.label} <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </main>
    </PublicLayout>
  );
};

export default FeaturePage;
